import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";

const ModeContext = createContext<{
  active: boolean;
  wide: boolean;
  register: (id: string, active: boolean, kind?: "record" | "entry") => void;
} | null>(null);

/** A mode entry temporarily yields its chrome to the document record, without
 * changing its route, dropping its state, or remounting the active workflow. */
export const DocumentModeBoundary: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const parentRegister = useContext(ModeContext)?.register;
  const [records, setRecords] = useState<Record<string, "record" | "entry">>(
    {}
  );
  const register = useCallback(
    (id: string, active: boolean, kind: "record" | "entry" = "record") => {
      parentRegister?.(id, active, kind);
      setRecords((previous) => {
        if (!!previous[id] === active) return previous;
        const next = { ...previous };
        if (active) next[id] = kind;
        else delete next[id];
        return next;
      });
    },
    [parentRegister]
  );
  const value = useMemo(
    () => ({
      active: Object.values(records).includes("record"),
      wide: Object.keys(records).length > 0,
      register,
    }),
    [records, register]
  );
  return <ModeContext.Provider value={value}>{children}</ModeContext.Provider>;
};

export const useDocumentModeCanvasWide = () =>
  useContext(ModeContext)?.wide ?? false;

/** The approved Today entry uses the full mode canvas, without hiding its chrome. */
export function useDocumentModeEntryCanvas(): void {
  const register = useContext(ModeContext)?.register;
  const id = useId();
  useEffect(() => {
    register?.(id, true, "entry");
    return () => register?.(id, false);
  }, [register, id]);
}

export const useDocumentModeActive = () =>
  useContext(ModeContext)?.active ?? false;

export function useDocumentModeRecord(): void {
  const register = useContext(ModeContext)?.register;
  const id = useId();
  useEffect(() => {
    register?.(id, true);
    return () => register?.(id, false);
  }, [register, id]);
}

export const DocumentModeChrome: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (useDocumentModeActive() ? null : <>{children}</>);
